import { AppConstants } from '@/helpers/app.constants';
import { UserHelper } from '@/helpers/user.helper';
import { AlertService } from '@/services/alert.service';
import { AuthenticationService } from '@/services/authentication.service';
import { BridgeService, Credentials } from '@/services/bridge/bridge.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import { MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { ErrorBusinessCode, UserPasswordChange, UsersService } from '@eemo/api-client';
import { Subscription } from 'rxjs';
import { BridgeInputService } from '@/services/bridge/bridge-input.service';
import { MatButtonModule } from '@angular/material/button';
import { MdbPopoverModule } from 'mdb-angular-ui-kit/popover';
import { NgIf } from '@angular/common';

import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TooltipDirective } from '@/directives/tooltip/tooltip.directive';
import { PasswordCheckComponent } from '../password-check/password-check.component';

@Component({
    selector: 'app-password-update-dialog',
    templateUrl: './password-update-dialog.component.html',
    styleUrls: ['./password-update-dialog.component.scss'],
    standalone: true,
    imports: [PasswordCheckComponent, MatDialogModule, FormsModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatIconModule, NgIf, MdbPopoverModule, MatButtonModule, TooltipDirective]
})
export class PasswordUpdateDialogComponent implements OnInit, OnDestroy {
  passwordForm: UntypedFormGroup;
  formSubmitting = false;

  hidePasswordActual = true;
  hidePassword = true;
  hidePasswordConfirm = true;

  credentialSub = new Subscription();

  constructor(
    private authenticationService: AuthenticationService,
    private usersService: UsersService,
    private fb: UntypedFormBuilder,
    private alertService: AlertService,
    private userHelper: UserHelper,
    private bridgeInputService: BridgeInputService,
    public dialogRef: MatDialogRef<PasswordUpdateDialogComponent>
  ) {}

  // convenience getter for easy access to form fields
  f(field: string): AbstractControl {
    return this.passwordForm.get(field);
  }

  get form_password() {
    return this.passwordForm.get('newPassword') as FormControl<string>;
  }

  ngOnInit() {
    this.initSubscriptions();
    this.passwordForm = this.fb.group({
      oldPassword: ['', [Validators.required]],
      newPassword: ['', [Validators.required, Validators.pattern(AppConstants.FORM_PATTERN_PASSWORD)]],
      passwordConfirmed: ['', Validators.required]
    });
    BridgeService.askCredentials(true);
  }

  ngOnDestroy(): void {
    this.credentialSub.unsubscribe();
  }

  onSubmit() {
    if (this.f('newPassword').value !== this.f('passwordConfirmed').value) {
      this.f('newPassword').setErrors({ passwordDontMatch: true });
      this.f('passwordConfirmed').setErrors({ passwordDontMatch: true });
    } else {
      this.f('newPassword').setErrors({});
      this.f('newPassword').updateValueAndValidity({ onlySelf: false });
      this.f('passwordConfirmed').setErrors({});
      this.f('passwordConfirmed').updateValueAndValidity({ onlySelf: false });
    }

    if (this.passwordForm.valid) {
      this.formSubmitting = true;
      const passwordUpdate: UserPasswordChange = {
        oldPassword: this.f('oldPassword').value,
        newPassword: this.f('newPassword').value
      };

      this.usersService
        .changeUserPassword(this.authenticationService.currentUserValue.userId, passwordUpdate)
        .subscribe(
          (result) => {
            this.dialogRef.close(true);
            this.alertService.success('Mot de passe modifié avec succès');
            BridgeService.saveCredentials({
              username: this.userHelper.currentUser.email,
              password: passwordUpdate.newPassword
            });
          },
          (err: HttpErrorResponse) => {
            if (err.error && err.error.businessCode) {
              switch (err.error.businessCode) {
                case ErrorBusinessCode.BAD_PASSWORD:
                  this.f('oldPassword').setErrors({ badPassword: true });
                  break;
                case ErrorBusinessCode.REFUSED_PASSWORD:
                  this.f('newPassword').setErrors({ refusedPassword: true });
                  break;
              }
              this.formSubmitting = false;
            } else {
              this.dialogRef.close(true);
            }
          }
        );
    }
  }
  onNoClick(): void {
    this.dialogRef.close(false);
  }

  initSubscriptions() {
    this.credentialSub = this.bridgeInputService.credentials$.subscribe((credential: Credentials) => {
      this.f('oldPassword').setValue(credential.password || '');
    });
  }
}
